/* =========================================================
   AUTO OBRAZY — cart benefits + delivery guard
   V147 — audit fixes:
     * delivery state is an explicit state machine backed by CART ATTRIBUTES,
       no longer DOM-text heuristics + sessionStorage guessing
     * pickup -> home clears the stale pickup point (address + id + type)
     * oversize comes from one shared resolver (property / size label /
       weight fallback), not three different formulas
     * checkout submit uses a real submit button, never a Globe-mutated one
     * a failed gift/protection reconcile stops checkout instead of passing
       an out-of-date cart to the payment step
     * MutationObserver is scoped to the Globe modules and batched in one rAF
   ========================================================= */

/* =========================================================
   FIX 193 — zastavenie nekonečnej slučky MutationObserver → apply()

   Problém: `observeModules()` sledoval v module atribút `class`, textové uzly
   a childList. `apply()` do TOHO ISTÉHO podstromu zapisoval `classList.toggle`
   a `pill.textContent`. `classList.toggle(name, force)` aj priradenie
   `textContent` zapíšu hodnotu VŽDY — aj keď je rovnaká — takže vznikol nový
   mutation record, ten zavolal `scheduleApply()`, ten v ďalšom rámci znova
   `apply()`. Slučka bežala 60× za sekundu donekonečna, každý beh navyše robil
   `getComputedStyle()` + `getClientRects()` (vynútený prepočet rozloženia).
   Košík je v hlavičke na KAŽDEJ stránke, takže sa to týkalo celého eshopu,
   len čo mal zákazník niečo v košíku.

   Riešenie: každý zápis do DOM je idempotentný — keď sa hodnota nemení,
   nezapisuje sa a žiadny mutation record nevznikne. Slučka tým zaniká.
   ========================================================= */

/* Prepne triedu len vtedy, keď sa stav naozaj mení. */
function aoToggleClass(element, name, force) {
  if (!element || !element.classList) return false;
  const want = Boolean(force);
  if (element.classList.contains(name) === want) return false;
  element.classList.toggle(name, want);
  return true;
}

/* Zapíše text len vtedy, keď je iný. */
function aoSetText(element, value) {
  if (!element) return false;
  const next = String(value);
  if (element.textContent === next) return false;
  element.textContent = next;
  return true;
}

/* Zapíše atribút len vtedy, keď je iný. */
function aoSetAttr(element, name, value) {
  if (!element) return false;
  if (value === null || value === false) {
    if (!element.hasAttribute(name)) return false;
    element.removeAttribute(name);
    return true;
  }
  const next = String(value);
  if (element.getAttribute(name) === next) return false;
  element.setAttribute(name, next);
  return true;
}

function aoSetHidden(element, hidden) {
  if (!element) return false;
  const want = Boolean(hidden);
  if (element.hidden === want) return false;
  element.hidden = want;
  return true;
}

function aoSetData(element, key, value) {
  if (!element || !element.dataset) return false;
  const next = String(value);
  if (element.dataset[key] === next) return false;
  element.dataset[key] = next;
  return true;
}

const PROTECTION_TIERS = Object.freeze([
  { min: 0, sku: 'SHIP-PROTECT-199', price: 199 },
  { min: 7000, sku: 'SHIP-PROTECT-399', price: 399 },
  { min: 14000, sku: 'SHIP-PROTECT-599', price: 599 },
  { min: 21000, sku: 'SHIP-PROTECT-799', price: 799 },
  { min: 28000, sku: 'SHIP-PROTECT-999', price: 999 },
  { min: 35000, sku: 'SHIP-PROTECT-1199', price: 1199 },
]);

const GIFT_SKU = 'FREE-GIFT-50';
const GIFT_THRESHOLD_FALLBACK = 4000;

/* Verzia pravidiel dopravy. Zapisuje sa do cart attributes, takže objednávka
   v admine vždy nesie informáciu, podľa akého cenníka bola vytvorená. */
const SHIPPING_RULE_VERSION_FALLBACK = '2026-09-01';

function shippingRuleVersion() {
  const node = typeof document !== 'undefined' ? document.getElementById('ao-cart-benefits-config') : null;
  if (node) {
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      const value = String(parsed.shippingRuleVersion || '').trim();
      if (value) return value;
    } catch (_) { /* fallback nižšie */ }
  }
  return SHIPPING_RULE_VERSION_FALLBACK;
}

/* Cart attributes, ktoré vlastní téma. */
const AO_ATTR = Object.freeze({
  mode: 'AO doprava',
  pointType: 'AO typ miesta',
  oversize: 'AO nadrozmer',
  rules: 'AO cenník dopravy',
});

/* Kľúče cart attributes patriace výberu výdajného miesta (naše aj tie, ktoré
   zapisuje Globe/Packeta). Pri prepnutí na doručenie domov sa musia
   vyprázdniť, inak checkout zdedí starú pobočkovú adresu. */
const PICKUP_ATTR_PATTERN = /(packeta|z-?box|z-?bot|pickup|výdaj|vydaj|globe|point|zásielkovn|zasielkovn)/i;

const OVERSIZE_THRESHOLD_GRAMS = 100000;

function pageCartCount() {
  const value = Number(window.AOPageContext && window.AOPageContext.cartCount);
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function runWhenIdle(callback, timeout = 1200) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout });
  } else {
    window.setTimeout(callback, Math.min(350, timeout));
  }
}

/* Jediný zdroj pravdy pre „nadrozmer" na strane klienta. Poradie je zámerné:
   explicitné označenie má vždy prednosť pred hmotnostným hackom. */
function itemIsOversize(item) {
  const properties = item?.properties || {};
  if (String(properties['_AO nadrozmer'] || '').toLowerCase() === 'true') return true;

  const haystack = [
    item?.variant_title,
    item?.title,
    Array.isArray(item?.options_with_values)
      ? item.options_with_values.map((option) => option?.value).join(' ')
      : '',
  ].join(' ').toLowerCase().replace(/\s+/g, '');

  if (/50cmx70cm|50x70|50×70|70x50/.test(haystack)) return true;

  /* `oversized` je strih trička, nie nadrozmer zásielky. Niektoré staršie
     varianty tričiek môžu mať kvôli historickému shipping hacku extrémnu
     hmotnosť; samotná váha preto nesmie zablokovať Z-BOX pri textile. */
  const productKind = [item?.product_type, item?.title, item?.handle, item?.url]
    .join(' ')
    .toLocaleLowerCase('sk-SK');
  if (/trič|tric|shirt|\btee\b/.test(productKind)) return false;

  return Number(item?.grams || 0) >= OVERSIZE_THRESHOLD_GRAMS;
}

function skuOf(item) {
  return String(item?.sku || '').trim().toUpperCase();
}

function isProtectionItem(item) {
  return skuOf(item).startsWith('SHIP-PROTECT-');
}

function isGiftItem(item) {
  return skuOf(item) === GIFT_SKU;
}

function isTechnicalItem(item) {
  const properties = item?.properties || {};
  return isProtectionItem(item)
    || isGiftItem(item)
    || Boolean(String(properties['_AO technický doplnok'] || '').trim())
    || String(properties['_AO exclude-cart-threshold'] || '').toLowerCase() === 'true';
}

function cartIsOversize(cart) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  return items.filter((item) => !isTechnicalItem(item)).some(itemIsOversize);
}

/* =========================================================
   DELIVERY GUARD
   ========================================================= */
class AOGlobeDeliveryGuard {
  constructor() {
    this.root = window.Shopify?.routes?.root || '/';
    /** @type {'home'|'pickup'|null} */
    this.mode = null;
    this.pointType = ''; // 'branch' | 'locker' | ''
    this.pointLabel = '';
    this.oversize = false;
    this.cartToken = '';
    this.bound = false;
    this.observers = [];
    this.frame = 0;
    this.settleTimer = 0;
    this.settleAttempt = 0;
    this.lastWritten = '';
    this.writeQueue = Promise.resolve(true);
    this.pendingDeliveryWrite = Promise.resolve(true);
    this.deliveryWritePending = false;
    this.deliveryWriteFailed = false;
    this.identityPromise = null;
    this.localIntent = null;
    this.localIntentUntil = 0;
    this.awaitingPickupChoice = false;
    this.pickupBaselines = [];
    this.pickupBaselinePromise = null;
  }

  /* ---------- DOM helpers ---------- */

  modules() {
    return Array.from(document.querySelectorAll('[data-ao-globe-module]'));
  }

  visibleModules() {
    const visible = this.modules().filter((module) => this.isVisible(module));
    return visible.length ? visible : this.modules();
  }

  /* V draweri aj na samostatnej stránke košíka môže Globe nechať v DOM dve
     kópie widgetu. Stav vždy čítame najprv z otvoreného dialógu; skrytá stará
     kópia tak nemôže prepísať práve zvolenú dopravu. */
  activeModules() {
    const openDialog = document.querySelector('dialog[open], [role="dialog"][aria-modal="true"]');
    if (openDialog) {
      const dialogModules = Array.from(openDialog.querySelectorAll('[data-ao-globe-module]'))
        .filter((module) => this.isVisible(module));
      if (dialogModules.length) return dialogModules;
    }
    return this.visibleModules();
  }

  checkoutButtons() {
    const selector = '.ao-globe-checkout-button, [data-ao-checkout], #checkout, button[name="checkout"]';
    return Array.from(document.querySelectorAll(selector))
      .filter((button) => !button.dataset.aoCheckoutProxy)
      .filter((button, index, list) => list.indexOf(button) === index);
  }

  isVisible(node) {
    if (!(node instanceof Element) || !node.isConnected) return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity || 1) === 0) return false;
    return node.getClientRects().length > 0;
  }

  normalize(value) {
    return String(value || '').toLocaleLowerCase('sk-SK').replace(/\s+/g, ' ').trim();
  }

  pickupFingerprint(value) {
    return this.normalize(value).replace(/[^a-z0-9áäčďéíĺľňóôŕšťúýž]+/gi, ' ').trim();
  }

  addPickupBaseline(value) {
    const fingerprint = this.pickupFingerprint(value);
    if (fingerprint && !this.pickupBaselines.includes(fingerprint)) this.pickupBaselines.push(fingerprint);
  }

  rememberPickupBaseline() {
    const signature = this.domPickupSignature();
    if (signature && signature.label) this.addPickupBaseline(signature.label);
    if (this.pointLabel) this.addPickupBaseline(this.pointLabel);
  }

  capturePickupBaselineFromCart() {
    this.pickupBaselinePromise = this.safeCart().then((cart) => {
      if (!this.awaitingPickupChoice || !cart) return;
      const state = this.readStateFromCart(cart);
      if (state.pointLabel) this.addPickupBaseline(state.pointLabel);
    }).catch(() => {}).finally(() => {
      this.pickupBaselinePromise = null;
    });
    return this.pickupBaselinePromise;
  }

  isPickupBaseline(value) {
    const fingerprint = this.pickupFingerprint(value);
    return Boolean(fingerprint && this.pickupBaselines.includes(fingerprint));
  }

  markLocalIntent(mode, duration = 3200) {
    this.localIntent = mode || null;
    this.localIntentUntil = this.localIntent ? Date.now() + duration : 0;
  }

  localIntentActive(mode) {
    if (!this.localIntent || Date.now() > this.localIntentUntil) {
      this.localIntent = null;
      this.localIntentUntil = 0;
      return false;
    }
    return mode ? this.localIntent === mode : true;
  }

  domDeliveryMode() {
    const selector = [
      'input[type="radio"]:checked',
      'input[type="checkbox"]:checked',
      '[role="radio"][aria-checked="true"]',
      '[aria-selected="true"]',
      '[aria-pressed="true"]',
      '[data-selected="true"]',
    ].join(',');

    let pickupSeen = false;
    for (const module of this.activeModules()) {
      /* Globe schováva samotné rádio (opacity:0), ale jeho rodičovská karta
         ostáva viditeľná. Checked stav preto čítame aj cez túto kartu. */
      for (const card of module.querySelectorAll('[data-delivery-method]')) {
        if (!this.isVisible(card)) continue;
        const selected = card.matches('[aria-selected="true"], [aria-pressed="true"], [data-selected="true"], .is-selected, .selected')
          || card.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
        if (!selected) continue;
        const action = this.classifyControl(card);
        if (action === 'home') return 'home';
        if (action === 'pickup' || action === 'pickup-open') pickupSeen = true;
      }
      for (const control of module.querySelectorAll(selector)) {
        if (!this.isVisible(control)) continue;
        const action = this.classifyControl(control);
        if (action === 'home') return 'home';
        if (action === 'pickup' || action === 'pickup-open') pickupSeen = true;
      }
    }
    return pickupSeen ? 'pickup' : '';
  }

  /* Globe vkladá niektoré rádiá bez programového názvu. Opravujeme ich v
     existujúcom module/observer cykle, bez ďalšieho globálneho observera. */
  repairControlLabels() {
    this.modules().forEach((module) => {
      module.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        const hasLabel = Boolean((input.labels && input.labels.length) || input.getAttribute('aria-label') || input.getAttribute('aria-labelledby'));
        if (hasLabel) return;
        const container = input.closest('label, [role="radio"], [class*="option"], [class*="method"], [class*="delivery"]') || input.parentElement;
        const text = this.normalize(container ? container.textContent : input.value);
        if (/doručenie domov|doručenie na adresu|na adresu|home delivery|kuriér|courier/.test(text)) {
          aoSetAttr(input, 'aria-label', 'Doručenie na adresu');
        } else if (/z-?box|z-?bot|výdajné miest|vydajne miest|odberné miest|odberne miest|výdejní míst|pickup|packeta/.test(text)) {
          aoSetAttr(input, 'aria-label', 'Z-BOX alebo výdajné miesto');
        }
      });
    });
  }

  /* FIX 212 — v košíku má byť pri výdajniach jasné, že sem patrí aj Z-BOX.
     Text vypisuje appka Globe, takže ho premenúvame v už existujúcom
     module/observer cykle. Meníme LEN textový uzol s presným popisom
     možnosti; hodnoty, ktoré appka posiela ďalej, zostávajú nedotknuté.
     Zásah je idempotentný — raz premenovaný uzol sa označí a druhýkrát
     sa naň nesiahne. */
  relabelPickupOption() {
    /* FIX 213 — „Výdajné miesta" musí v košíku hovoriť aj o Z-BOXe.

       Prvá verzia hľadala ELEMENT, ktorého celý text je presne ten popis.
       Globe ale kreslí kartu ako `<button><svg/>Výdajné miesta</button>` —
       element má potomka (ikonu), takže test `childElementCount > 0` ho
       zakaždým preskočil a nič sa nepremenovalo. Teraz prechádzame priamo
       TEXTOVÉ uzly, čo funguje bez ohľadu na to, ako appka kartu poskladá.

       Cyklus `apply()` beží pri každej mutácii widgetu, preto je scan
       obmedzený na štyrikrát za sekundu. */
    const now = Date.now();
    if (this.pickupLabelAt && now - this.pickupLabelAt < 260) return;
    this.pickupLabelAt = now;

    const wanted = 'Výdajné miesta / Z-BOX';
    const exact = /^(výdajné miesta|výdajné miesto|vydajne miesta|vydajne miesto|odberné miesta|odberné miesto|odberne miesta|odberne miesto|výdejní místa|výdejní místo|pickup point|pickup points)$/i;

    /* Keď by Globe kreslil mimo nášho slotu, prehľadáme aj košík —
       inak by premenovanie ticho nič neurobilo. */
    const scopes = this.modules().slice();
    document.querySelectorAll('cart-drawer-component, .cart-drawer, #MainContent .cart, form[action*="/cart"]').forEach((node) => {
      if (node && scopes.indexOf(node) === -1) scopes.push(node);
    });

    scopes.forEach((scope) => {
      if (!scope || !scope.isConnected) return;
      const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null);
      const hits = [];
      let node = walker.nextNode();
      while (node) {
        const raw = String(node.nodeValue || '').replace(/\s+/g, ' ').trim();
        if (raw && raw.length <= 40 && exact.test(raw)) hits.push(node);
        node = walker.nextNode();
      }
      /* Zápis až po prejdení stromu: meniť uzly počas chôdze by walker
         zbytočne rozhádzalo. Hodnota sa prepisuje len keď je naozaj iná,
         inak by každý zápis vyrobil mutation record a roztočil observer. */
      hits.forEach((textNode) => {
        if (textNode.nodeValue !== wanted) textNode.nodeValue = wanted;
      });
    });
  }

  /* V214 — „Poštovné od …" priamo v dlaždici spôsobu doručenia.

     Text sa NEVKLADÁ do DOM ako element. Zapíšeme len atribút
     `data-ao-rate-label` a vykreslí ho CSS cez `::after` s `content: attr(...)`.
     Dôvod je vecný: obsah pseudoelementu nie je súčasťou `textContent`, takže
     sa nepletie do `classifyControl()` ani do `meaningfulSignature()`, ktoré
     stav dopravy čítajú z textu dlaždice. Zápis atribútu navyše nespadá do
     `attributeFilter` observera, takže nevyrobí ani jeden mutation record —
     žiadna spätná väzba do `apply()`.

     Sadzba sa vyberá podľa krajiny (SK / CZ) a podľa toho, či je v košíku
     nadrozmer; obe hodnoty už pozná táto trieda, takže sa prepíšu aj bez
     prekreslenia sekcie. */
  /* Odomknutá doprava zdarma — jediný zdroj pravdy je trieda `is-unlocked`,
     ktorú nastavuje aj server (Liquid), aj prepočet po AJAX zmene košíka. */
  freeShippingUnlocked() {
    return !!document.querySelector(
      '[data-ao-reward-bar].is-unlocked, .ao-cart-shipping-v5.is-unlocked, .ao-cart-shipping.is-unlocked'
    );
  }

  renderShippingHints() {
    const now = Date.now();
    if (this.rateHintAt && now - this.rateHintAt < 260) return;
    this.rateHintAt = now;

    const shopCountry = String((window.Shopify && window.Shopify.country) || '').toUpperCase();

    this.modules().forEach((module) => {
      const data = module.dataset || {};
      if (!data.aoRateSkPickup && !data.aoRateSkHome) return;

      const country = (shopCountry || String(data.aoRateCountry || 'SK').toUpperCase()) === 'CZ' ? 'Cz' : 'Sk';
      const suffix = this.oversize ? 'Oversize' : '';
      /* V232: keď je doprava zdarma odomknutá, je nezmysel písať do dlaždice
         „Poštovné od 3,70 €" — zákazník práve splnil hranicu a to číslo
         nezaplatí. Stav berieme z lišty odmeny, ktorú prekresľuje hlavička
         pri každej zmene košíka, takže je vždy aktuálny. */
      const labels = this.freeShippingUnlocked()
        ? { pickup: 'Doprava zdarma', home: 'Doprava zdarma' }
        : {
          pickup: data[`aoRate${country}Pickup${suffix}`] || data[`aoRate${country}Pickup`] || '',
          home: data[`aoRate${country}Home${suffix}`] || data[`aoRate${country}Home`] || '',
        };

      /* Dlaždicu berieme PRVÚ v poradí dokumentu — je to ten najvrchnejší
         obal voľby. Vnorené tlačidlá by inak dostali rovnaký popisok. */
      const chosen = { pickup: null, home: null };
      module.querySelectorAll('[data-delivery-method], label, button, [role="radio"], [role="button"]').forEach((node) => {
        if (chosen.pickup && chosen.home) return;
        if (!this.isVisible(node)) return;
        const kind = this.classifyControl(node);
        if (kind !== 'pickup' && kind !== 'home') return;
        if (chosen[kind]) return;
        /* Malý prvok (napr. odkaz „Zmeniť") nie je dlaždica. */
        if (node.getBoundingClientRect().width < 70) return;
        chosen[kind] = node;
      });

      ['pickup', 'home'].forEach((kind) => {
        const tile = chosen[kind];
        if (!tile) return;
        const label = labels[kind];
        if (!label) {
          if (tile.dataset.aoRateLabel) delete tile.dataset.aoRateLabel;
          return;
        }
        if (tile.dataset.aoRateLabel !== label) tile.dataset.aoRateLabel = label;
      });
    });
  }

  /* Widget je pripravený, keď Globe naozaj vykreslil obsah do nášho slotu. */
  widgetReady() {
    return this.visibleModules().some((module) => {
      const slot = module.querySelector('[data-ao-globe-pickup-slot]');
      if (slot && slot.childElementCount > 0) return true;
      return module.querySelector('input[type="radio"], button, [role="radio"], [role="button"]') !== null;
    });
  }

  /* ---------- pickup detection (DOM as a secondary signal only) ---------- */

  domPickupSignature() {
    for (const module of this.activeModules()) {
      const marked = module.querySelector('[data-pickup-point-id], [data-pickup-id], [data-point-id], [data-selected-point-id]');
      if (marked) {
        const id = marked.getAttribute('data-pickup-point-id')
          || marked.getAttribute('data-pickup-id')
          || marked.getAttribute('data-point-id')
          || marked.getAttribute('data-selected-point-id')
          || '';
        return this.meaningfulSignature(String(id).trim(), this.normalize(marked.textContent));
      }
    }

    /* Globe zobrazí tlačidlo „Zmeniť" až po výbere konkrétneho bodu. */
    for (const module of this.activeModules()) {
      const trigger = Array.from(module.querySelectorAll('button, a, [role="button"]')).find((node) => {
        if (!this.isVisible(node)) return false;
        const text = this.normalize(`${node.getAttribute('aria-label') || ''} ${node.textContent || ''}`);
        return text === 'zmeniť' || text === 'change' || text.includes('zmeniť miesto') || text.includes('change pickup');
      });
      if (trigger) {
        const card = trigger.closest('[class*="point"], [class*="selected"], li, article, div') || module;
        return this.meaningfulSignature('', this.normalize(card.textContent));
      }
    }
    return null;
  }

  /* Prázdny alebo príliš krátky popis nie je dôkaz o vybranom bode.
     Bez tejto kontroly stačilo, aby Globe vykreslil prázdny kontajner
     s `data-pickup-point-id`, a checkout sa odomkol bez výberu. */
  meaningfulSignature(id, rawLabel) {
    const label = String(rawLabel || '').slice(0, 240);
    if (!id && label.length < 6) return null;
    return { id, label };
  }

  classifyPointType(label) {
    const text = this.normalize(label);
    if (!text) return '';
    if (/z[\s-]?box|z[\s-]?bot|parcel locker|\blocker\b|automat/.test(text)) return 'locker';
    return 'branch';
  }

  /* ---------- cart attributes = source of truth ---------- */

  readStateFromCart(cart) {
    const attributes = cart?.attributes || {};
    let mode = this.normalize(attributes[AO_ATTR.mode]);
    if (mode !== 'home' && mode !== 'pickup') mode = '';

    let pointType = this.normalize(attributes[AO_ATTR.pointType]);
    if (pointType !== 'branch' && pointType !== 'locker') pointType = '';

    let pointLabel = '';
    Object.keys(attributes).forEach((key) => {
      if (Object.values(AO_ATTR).indexOf(key) !== -1) return;
      if (!PICKUP_ATTR_PATTERN.test(key)) return;
      const value = String(attributes[key] || '').trim();
      if (value) pointLabel += ` ${key} ${value}`;
    });

    /* AO atribút zapisujeme spolu s Globe atribútom `Doprava`, preto je pri
       konflikte autoritatívny. `Doprava` používame iba ako migráciu starších
       košíkov, ktoré AO stav ešte nemajú. */
    const deliveryAttribute = this.normalize(
      attributes.Doprava || attributes.Delivery || attributes['Spôsob dopravy'] || ''
    );
    if (!mode) {
      if (/doručenie domov|doručenie na adresu|na adresu|home delivery|kuriér|courier/.test(deliveryAttribute)) {
        mode = 'home';
      } else if (/z-?box|z-?bot|výdajné miest|vydajne miest|odberné miest|odberne miest|výdejní míst|pickup|packeta/.test(deliveryAttribute)) {
        mode = 'pickup';
      } else if (pointLabel.trim()) {
        mode = 'pickup';
      }
    }
    if (mode === 'home') {
      pointLabel = '';
      pointType = '';
    }

    return { mode, pointLabel: pointLabel.trim(), pointType };
  }

  pickupAttributeKeys(cart) {
    return Object.keys(cart?.attributes || {}).filter((key) => {
      if (Object.values(AO_ATTR).indexOf(key) !== -1) return false;
      return PICKUP_ATTR_PATTERN.test(key);
    });
  }

  async writeAttributes(attributes) {
    const payload = JSON.stringify(attributes);
    if (payload === this.lastWritten) return true;
    const run = this.writeQueue.then(async () => {
      const response = await fetch(`${this.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes }),
      });
      if (!response.ok) throw new Error(`delivery-attributes-${response.status}`);
      this.lastWritten = payload;
      return true;
    });
    this.writeQueue = run.catch(() => false);
    return run;
  }

  async persistMode(mode, options) {
    const settings = options || {};
    const attributes = {};
    attributes[AO_ATTR.mode] = mode || '';
    attributes[AO_ATTR.pointType] = mode === 'pickup' ? this.pointType : '';
    attributes[AO_ATTR.oversize] = this.oversize ? 'true' : 'false';
    attributes[AO_ATTR.rules] = shippingRuleVersion();
    /* Tento kľúč číta aj Packeta/Globe Liquid. Obe reprezentácie sa musia
       zapísať v jednom requeste, inak po ďalšom produkte ožije starý stav. */
    attributes.Doprava = mode === 'home' ? 'Kuriér na adresu' : (mode === 'pickup' ? 'Výdajné miesto' : '');

    if (settings.clearPickup) {
      const source = settings.cart || await this.safeCart();
      this.pickupAttributeKeys(source).forEach((key) => { attributes[key] = ''; });
    }

    await this.writeAttributes(attributes);
    /* Okamžite aj do lokálnej kópie, nech pill a texty nečakajú na ďalší
       fetch — výber dopravy sa musí prejaviť hneď pri kliknutí. */
    try {
      window.AOCartAttributes = Object.assign({}, window.AOCartAttributes || {}, attributes);
    } catch (error) {}
  }

  async safeCart() {
    try {
      const response = await fetch(`${this.root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  async syncFromCart(cartSnapshot = null) {
    if (this.identityPromise) return this.identityPromise;
    this.identityPromise = (async () => {
      if (this.awaitingPickupChoice && this.pickupBaselinePromise) {
        await this.pickupBaselinePromise;
      }
      const cart = cartSnapshot && Array.isArray(cartSnapshot.items)
        && typeof cartSnapshot.item_count === 'number' && cartSnapshot.attributes
        ? cartSnapshot
        : await this.safeCart();
      if (!cart) return;
      /* V251: window.AOCartAttributes doteraz vznikalo raz, pri vykreslení
         stránky v theme.liquid, a už sa nikdy neaktualizovalo. Keď si teda
         zákazník v košíku prepol dopravu, tento objekt držal starú hodnotu
         a všetko, čo z neho číta, tvrdilo staré. Obnovujeme ho pri každom
         načítaní košíka. */
      try { window.AOCartAttributes = cart.attributes || {}; } catch (error) {}
      this.cartToken = String(cart.token || '').trim();
      this.oversize = cartIsOversize(cart);
      this.applyOversizeToDom();

      const state = this.readStateFromCart(cart);
      const domMode = this.domDeliveryMode();
      const domPickup = this.domPickupSignature();
      const localIntent = this.localIntentActive() ? this.localIntent : '';

      /* Čerstvý klik používateľa má najvyššiu prioritu. Až keď lokálny intent
         vyprší, smie ho prepísať DOM alebo cart attributes. Tak sa pickup/home
         neprepisujú navzájom počas asynchrónneho zápisu Globe widgetu. */
      if (localIntent === 'home') {
        this.mode = 'home';
        this.pointType = '';
        this.pointLabel = '';
        this.awaitingPickupChoice = false;
        this.pickupBaselines = [];
        this.pickupBaselinePromise = null;
      } else if (localIntent === 'pickup' || (this.mode === 'pickup' && this.awaitingPickupChoice)) {
        this.mode = 'pickup';

        if (domPickup && (!this.awaitingPickupChoice || !this.isPickupBaseline(domPickup.label))) {
          this.pointLabel = domPickup.label;
          this.pointType = this.classifyPointType(domPickup.label);
          this.awaitingPickupChoice = false;
          this.pickupBaselines = [];
          this.pickupBaselinePromise = null;
        } else if (this.awaitingPickupChoice) {
          /* Po otvorení mapy nesmie staré uložené miesto znovu odomknúť
             checkout. Ak sa Globe zapisuje cez portál mimo DOM, prijmeme až
             cart hodnotu, ktorá sa reálne zmenila oproti predošlému bodu. */
          if (state.pointLabel && !this.isPickupBaseline(state.pointLabel)) {
            this.pointLabel = state.pointLabel;
            this.pointType = state.pointType || this.classifyPointType(state.pointLabel);
            this.awaitingPickupChoice = false;
            this.pickupBaselines = [];
            this.pickupBaselinePromise = null;
          } else {
            this.pointLabel = '';
            this.pointType = '';
          }
        } else if (state.pointLabel) {
          this.pointLabel = state.pointLabel;
          this.pointType = state.pointType || this.classifyPointType(state.pointLabel);
        }
      } else if (state.mode === 'home') {
        this.mode = 'home';
        this.pointType = '';
        this.pointLabel = '';
        this.awaitingPickupChoice = false;
        this.pickupBaselines = [];
        this.pickupBaselinePromise = null;
      } else if (state.mode === 'pickup' || state.pointLabel) {
        this.mode = 'pickup';
        this.pointLabel = state.pointLabel;
        this.pointType = state.pointType || this.classifyPointType(state.pointLabel);
        this.awaitingPickupChoice = false;
        this.pickupBaselines = [];
        this.pickupBaselinePromise = null;
      } else if (domMode === 'home') {
        this.mode = 'home';
        this.pointType = '';
        this.pointLabel = '';
        this.awaitingPickupChoice = false;
        this.pickupBaselines = [];
        this.pickupBaselinePromise = null;
      } else if (domMode === 'pickup') {
        this.mode = 'pickup';
        this.pointLabel = domPickup?.label || '';
        this.pointType = domPickup ? this.classifyPointType(domPickup.label) : '';
      } else {
        this.mode = null;
        this.pointType = '';
        this.pointLabel = '';
      }
      this.apply();
    })();
    try {
      await this.identityPromise;
    } finally {
      this.identityPromise = null;
    }
  }

  applyOversizeToDom() {
    const value = this.oversize ? 'true' : 'false';
    this.modules().forEach((module) => {
      aoSetData(module, 'aoOversize', value);
    });
  }

  /* ---------- state machine ---------- */

  resolvedPickup() {
    if (this.mode === 'home') return null;

    const signature = this.domPickupSignature();
    if (signature) {
      if (this.awaitingPickupChoice && this.isPickupBaseline(signature.label)) return null;
      const type = this.classifyPointType(signature.label);
      this.pointLabel = signature.label;
      this.pointType = type;
      this.mode = 'pickup';
      this.awaitingPickupChoice = false;
      this.pickupBaselines = [];
      this.pickupBaselinePromise = null;
      return { label: signature.label, type };
    }

    if (!this.awaitingPickupChoice && this.pointLabel) {
      return { label: this.pointLabel, type: this.pointType || this.classifyPointType(this.pointLabel) };
    }
    return null;
  }

  selectionSatisfied() {
    if (!this.widgetReady()) return false;
    if (this.mode === 'home') return true;

    const pickup = this.resolvedPickup();
    if (!pickup) return false;
    /* Pri nadrozmere vyžadujeme POZITÍVNY dôkaz, že ide o klasickú pobočku.
       Neznámy typ je bezpečnejšie zamknúť než poslať 50×70 do Z-BOXu. */
    if (this.oversize && pickup.type !== 'branch') return false;
    return true;
  }

  deliveryAttrMode() {
    /* Zdrojom pravdy je atribút košíka „AO doprava" zo stavového automatu
       z V147 — nie to, čo si appka vykreslila do DOM. */
    try {
      const v = String((window.AOCartAttributes && window.AOCartAttributes['AO doprava']) || '').toLowerCase();
      if (v.indexOf('adres') > -1 || v === 'home') return 'home';
      if (v.indexOf('miest') > -1 || v.indexOf('box') > -1 || v === 'pickup') return 'pickup';
    } catch (error) {}
    return '';
  }

  isSatisfied() {
    /*
     * V225: košík, v ktorom nie je nič na odoslanie (napríklad iba
     * obraz na mieru), nemá čo riešiť s dopravou. Bez tejto výnimky by guard
     * zamkol pokladňu a čakal na výber výdajného miesta, ktoré Shopify ani
     * neponúkne. Hodnota prichádza z Liquidu (cart.requires_shipping), takže
     * to nie je odhad z DOM, ale fakt z košíka.
     */
    if (window.AOCartRequiresShipping === false) return true;
    return !this.deliveryWritePending && !this.deliveryWriteFailed && this.selectionSatisfied();
  }

  failureMessage() {
    if (this.deliveryWritePending) return 'Ukladám zvolený spôsob doručenia. Počkaj, prosím, chvíľu.';
    if (this.deliveryWriteFailed) return 'Voľbu dopravy sa nepodarilo uložiť. Skontroluj pripojenie a vyber spôsob doručenia znova.';
    const pickup = this.resolvedPickup();
    if (this.oversize && pickup && pickup.type === 'locker') {
      return 'Formát 50 × 70 sa do Z-BOXu nezmestí. Vyber klasické výdajné miesto alebo doručenie na adresu.';
    }
    if (this.oversize && pickup && pickup.type !== 'branch') {
      return 'Zvolené miesto sa nepodarilo overiť. Vyber ho, prosím, znova — pri formáte 50 × 70 musí ísť o klasické výdajné miesto.';
    }
    if (this.oversize) {
      return 'Vyber doručenie na adresu alebo klasické výdajné miesto. Pri formáte 50 × 70 nie je Z-BOX dostupný.';
    }
    return 'Vyber doručenie na adresu alebo konkrétny Z-BOX / výdajné miesto.';
  }

  setMode(mode, options) {
    const settings = options || {};
    const next = mode === 'home' || mode === 'pickup' ? mode : null;

    if (next === 'pickup' && settings.awaitingSelection) {
      this.rememberPickupBaseline();
      this.awaitingPickupChoice = true;
      this.capturePickupBaselineFromCart();
      this.pointLabel = '';
      this.pointType = '';
    } else if (next === 'home') {
      this.awaitingPickupChoice = false;
      this.pickupBaselines = [];
      this.pickupBaselinePromise = null;
      this.pointLabel = '';
      this.pointType = '';
    }

    this.mode = next;
    this.markLocalIntent(next);

    /* UI sa zmení okamžite. Sieťový zápis nesmie byť podmienkou toho, aby
       zákazník videl správny zelený/červený stav. */
    this.apply();

    if (next) {
      const clearPickup = next === 'home';
      this.deliveryWritePending = true;
      this.deliveryWriteFailed = false;
      this.apply();
      this.pendingDeliveryWrite = this.persistMode(next, { clearPickup }).then(() => {
        this.deliveryWritePending = false;
        this.deliveryWriteFailed = false;
        return this.syncFromCart().then(() => true);
      }).catch(() => {
        this.deliveryWritePending = false;
        this.deliveryWriteFailed = true;
        this.apply();
        return false;
      });
    }
  }

  /* ---------- rendering ---------- */

  apply() {
    this.repairControlLabels();
    this.relabelPickupOption();
    this.renderShippingHints();
    const domMode = this.domDeliveryMode();
    const localIntent = this.localIntentActive() ? this.localIntent : '';
    if (localIntent === 'home') {
      this.mode = 'home';
      this.pointLabel = '';
      this.pointType = '';
    } else if (localIntent === 'pickup') {
      this.mode = 'pickup';
    } else if (this.deliveryAttrMode() === 'home') {
      /*
       * V251: uložený atribút košíka je jediný zdroj, ktorý hovorí, čo si
       * zákazník naozaj vybral. Musí mať prednosť pred tým, čo má widget
       * práve zaškrtnuté v DOM — inak košík tvrdil „VÝDAJNÉ MIESTO VYBRANÉ"
       * človeku, ktorý si zvolil doručenie na adresu.
       */
      this.mode = 'home';
      this.pointLabel = '';
      this.pointType = '';
    } else if (this.deliveryAttrMode() === 'pickup') {
      this.mode = 'pickup';
    } else if (!this.mode && domMode === 'home' && !this.awaitingPickupChoice && this.deliveryAttrMode() === 'home') {
      /*
       * V226: guard si predtým privlastnil „doručenie na adresu" hneď, ako
       * appka Globe vykreslila tú možnosť ako zaškrtnutú — takže hlásil
       * „DORUČENIE NA ADRESU VYBRANÉ" ešte skôr, než zákazník na čokoľvek
       * klikol, a pri prepínaní si potom protirečil. Teraz sa na režim
       * z DOM spolieha len vtedy, keď ho potvrdzuje aj uložený atribút
       * košíka, teda keď to naozaj niekto vybral.
       */
      this.mode = 'home';
      this.pointLabel = '';
      this.pointType = '';
    } else if (!this.mode && domMode === 'pickup' && this.resolvedPickup()) {
      /*
       * V251: predtým stačilo, že widget mal zaškrtnutú možnosť výdajne —
       * a to má často hneď po vykreslení, bez akéhokoľvek kliknutia. Košík
       * tak hlásil vybranú výdajňu, ktorú nikto nevybral. Teraz musí byť
       * naozaj rozpoznané konkrétne miesto.
       */
      this.mode = 'pickup';
    }

    const selectionOk = this.selectionSatisfied();
    const ok = this.isSatisfied();
    const pickup = this.resolvedPickup();
    const deliveryResolved = Boolean(selectionOk && (
      this.mode === 'home' || (this.mode === 'pickup' && pickup)
    ));
    const oversizeResolved = this.oversize && deliveryResolved;

    this.modules().forEach((module) => {
      /* FIX 193: idempotentne — inak si observer sám vyrobí ďalší cyklus. */
      aoToggleClass(module, 'is-delivery-resolved', deliveryResolved);
      aoToggleClass(module, 'is-delivery-unresolved', !deliveryResolved);
      aoToggleClass(module, 'is-oversize-resolved', Boolean(oversizeResolved));

      const pill = module.querySelector('.ao-globe-brand__pill');
      if (pill) {
        let pillText;
        if (deliveryResolved) {
          if (this.mode === 'home') {
            pillText = '✓ DORUČENIE NA ADRESU VYBRANÉ';
          } else if (pickup && pickup.type === 'locker') {
            pillText = '✓ Z-BOX VYBRANÝ';
          } else {
            pillText = '✓ VÝDAJNÉ MIESTO VYBRANÉ';
          }
        } else if (this.oversize && pickup && pickup.type === 'locker') {
          pillText = 'Z-BOX NIE JE PRE 50 × 70';
        } else {
          pillText = 'TREBA VYBRAŤ DOPRAVU';
        }
        aoSetText(pill, pillText);
      }
    });

    this.checkoutButtons().forEach((button) => {
      aoToggleClass(button, 'is-globe-locked', !ok);
      if (button.dataset.aoRequiresJsUnlock === 'true' && button.disabled) {
        button.disabled = false;
      }
      if (ok) {
        aoSetAttr(button, 'aria-disabled', null);
        aoSetAttr(button, 'data-ao-globe-locked', null);
      } else {
        aoSetAttr(button, 'aria-disabled', 'true');
        aoSetAttr(button, 'data-ao-globe-locked', 'true');
      }
    });
    if (ok) this.clearErrors();
    return ok;
  }

  scheduleApply() {
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0;
      this.apply();
    });
  }

  /* Globe zapisuje výber asynchrónne. Namiesto burstu deviatich timerov
     spravíme jednu kontrolu potom, čo sa DOM upokojí. */
  settle(delay = 180) {
    window.clearTimeout(this.settleTimer);
    this.settleAttempt = 0;

    const probe = async () => {
      this.settleAttempt += 1;
      await this.syncFromCart();
      if (this.mode === 'pickup' && this.pointLabel && !this.awaitingPickupChoice) {
        await this.persistMode('pickup');
      }
      this.apply();

      /* Packeta/Globe môže zapísať nový pickup point s oneskorením. Krátko ho
         preto dobehneme aj bez ďalšieho kliku používateľa. Max 4 kontroly. */
      if (this.mode !== 'home' && !this.isSatisfied() && this.settleAttempt < 4) {
        this.settleTimer = window.setTimeout(probe, this.settleAttempt === 1 ? 320 : 520);
      }
    };

    this.settleTimer = window.setTimeout(probe, delay);
  }

  clearErrors() {
    this.modules().forEach((module) => {
      aoToggleClass(module, 'is-globe-nagging', false);
      const message = module.querySelector('[data-ao-globe-required-message]');
      aoSetHidden(message, true);
    });
  }

  contextModule(trigger) {
    const scope = (trigger && trigger.closest && trigger.closest('.cart-drawer, .ao-cart-page, .main-cart')) || document;
    return (scope.querySelector && scope.querySelector('[data-ao-globe-module]')) || this.visibleModules()[0] || null;
  }

  nag(trigger) {
    const pickup = this.resolvedPickup();
    window.AOAnalytics?.track?.('delivery_locked', {
      mode: this.mode || 'none',
      oversize: this.oversize,
      point_type: pickup ? pickup.type || 'unknown' : 'none',
      widget_ready: this.widgetReady(),
    });

    const module = this.contextModule(trigger);
    if (!module) return;
    const message = module.querySelector('[data-ao-globe-required-message]');
    if (message) {
      message.textContent = this.failureMessage();
      message.hidden = false;
    }
    module.classList.remove('is-globe-nagging');
    void module.offsetWidth;
    module.classList.add('is-globe-nagging');
    window.setTimeout(() => module.classList.remove('is-globe-nagging'), 650);
    try { module.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch (_) { module.scrollIntoView(); }
  }

  ensure(trigger) {
    if (this.apply()) return true;
    this.nag(trigger);
    return false;
  }

  async beforeCheckout(trigger) {
    const persisted = await this.pendingDeliveryWrite.catch(() => false);
    if (persisted) await this.syncFromCart();
    if (!persisted || !this.apply()) {
      this.nag(trigger);
      return false;
    }
    return true;
  }

  /* ---------- interaction ---------- */

  /* Klasifikujeme NAJBLIŽŠÍ ovládací prvok, nie osem rodičov. Obal Globe
     obsahuje text oboch kariet, takže hľadanie po rodičoch vracalo náhodný
     výsledok a doručenie domov ostávalo zamknuté. */
  classifyControl(target) {
    const control = target && target.closest
      ? target.closest('[data-delivery-method], label, button, a, [role="radio"], [role="button"], [role="tab"], input')
      : null;
    if (!control) return '';

    /* Globe vykresľuje celé voľby ako DIV[data-delivery-method]; skryté rádio
       nemá klikateľný pointer. Toto je presný a jazykovo nezávislý signál. */
    const methodControl = target.closest('[data-delivery-method]')
      || (control.matches && control.matches('[data-delivery-method]') ? control : null);
    const directText = this.normalize(`${control.getAttribute ? control.getAttribute('aria-label') || '' : ''} ${control.textContent || ''}`);
    if (/^zmeniť|^change/.test(directText)) return 'pickup-open';
    const explicitMethod = this.normalize(
      (methodControl && methodControl.getAttribute('data-delivery-method'))
      || (control.getAttribute && control.getAttribute('data-delivery-method'))
      || control.value
      || ''
    );
    if (/^(home|address|courier)$/.test(explicitMethod)) return 'home';
    if (/^(pickup|point|branch|locker|z-?box)$/.test(explicitMethod)) return 'pickup';

    let text = this.normalize(`${control.getAttribute ? control.getAttribute('aria-label') || '' : ''} ${control.textContent || ''}`);
    if (control.tagName === 'INPUT') {
      const label = (control.labels && control.labels[0]) || control.closest('label');
      text = this.normalize(`${control.getAttribute('aria-label') || ''} ${label ? label.textContent : ''} ${control.value || ''}`);
    }
    if (!text) return '';

    const home = /doručenie domov|doručenie na adresu|na adresu|home delivery|kuriér|courier/.test(text);
    const pickup = /z-?box|z-?bot|výdajné miest|vydajne miest|odberné miest|odberne miest|výdejní míst|pickup point|pickup points|packeta/.test(text);
    const change = /^zmeniť|^change/.test(text);

    if (change) return 'pickup-open';
    if (home && !pickup) return 'home';
    if (pickup && !home) return 'pickup';
    return '';
  }

  handleInteraction(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const module = target.closest('[data-ao-globe-module]');

    /* 1) Explicitný stav zaškrtnutého inputu má prednosť pred textom. */
    if (module) {
      const input = target.closest('input[type="radio"], input[type="checkbox"]');
      if (input && input.checked) {
        const action = this.classifyControl(input);
        if (action === 'home') { this.setMode('home'); return; }
        if (action === 'pickup') { this.setMode('pickup'); this.settle(); return; }
        if (action === 'pickup-open') { this.setMode('pickup', { awaitingSelection: true }); this.settle(); return; }
      }

      const action = this.classifyControl(target);
      if (action === 'home') { this.setMode('home'); return; }
      if (action === 'pickup') { this.setMode('pickup'); this.settle(); return; }
      if (action === 'pickup-open') {
        /* Otvorenie mapy ešte nie je výber bodu. */
        this.setMode('pickup', { awaitingSelection: true });
        this.settle();
        return;
      }
    }

    /* 2) Mapa Globe môže byť portálovaná mimo košíka — vtedy počkáme, kým
          appka zapíše výsledok, a prečítame ho z košíka. */
    const text = this.normalize(`${target.getAttribute ? target.getAttribute('aria-label') || '' : ''} ${target.textContent || ''}`);
    if (/vybrať|select|potvrdiť|confirm|z-?box|výdaj|vydaj|packeta|pickup/.test(text)) {
      this.settle();
    }
  }

  observeModules() {
    /* Zruš len modulové observery, boot observer ostáva. */
    this.observers = this.observers.filter((entry) => {
      if (entry.scope !== 'module') return true;
      entry.observer.disconnect();
      return false;
    });

    const callback = () => {
      this.scheduleApply();
      /* Výber bodu sa často prejaví mutáciou widgetu ešte pred cart eventom. */
      if (this.mode !== 'home') this.settle(140);
    };
    this.modules().forEach((module) => {
      const observer = new MutationObserver(callback);
      observer.observe(module, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        /* FIX 193: 'class' a 'hidden' si do modulu zapisuje `apply()` sám,
           takže ich sledovanie robilo spätnú väzbu. Stav výberu dopravy nesú
           `checked` / `aria-*` / `data-*` atribúty Globe widgetu — tie stačia. */
        attributeFilter: [
          'checked', 'aria-selected', 'aria-pressed', 'aria-checked',
          'data-selected', 'data-pickup-point-id', 'data-pickup-id', 'data-point-id', 'data-selected-point-id',
        ],
      });
      this.observers.push({ scope: 'module', observer });
    });
  }

  bind() {
    if (this.bound) return;
    this.bound = true;

    document.addEventListener('click', (event) => this.handleInteraction(event), true);
    document.addEventListener('change', (event) => this.handleInteraction(event), true);

    /* V232: lištu odmeny prekresľuje hlavička až po dotiahnutí /cart.js.
       Bez tohto signálu by v dlaždici dopravy chvíľu ešte svietila cena
       poštovného, hoci zákazník už má dopravu zdarma. */
    document.addEventListener('ao:reward-painted', () => {
      this.rateHintAt = 0;
      this.renderShippingHints();
    });

    /* Tvrdá zábrana: aj keď iný skript odstráni `disabled`, checkout nesmie
       odísť bez zvolenej dopravy. */
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const checkout = target && target.closest
        ? target.closest('.ao-globe-checkout-button, [data-ao-checkout], #checkout, button[name="checkout"]')
        : null;
      if (!checkout || checkout.dataset.aoCheckoutProxy) return;
      if (!checkout.closest('.cart-drawer, .ao-cart-page, .main-cart')) return;
      /* Platný nový výber pustíme do benefits managera; ten počká na pending
         cart/update.js a prvý klik potom pokračuje bez nutnosti dvojkliku. */
      if (this.selectionSatisfied()) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      this.nag(checkout);
      this.apply();
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;
      const submitter = event.submitter instanceof Element ? event.submitter : null;
      const checkout = submitter && submitter.matches
        && submitter.matches('.ao-globe-checkout-button, [data-ao-checkout], #checkout, button[name="checkout"]')
        ? submitter
        : null;
      if (!checkout || this.selectionSatisfied()) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      this.nag(checkout);
      this.apply();
    }, true);

    /* Globe pripája widget až po načítaní. Boot observer sleduje iba pridané
       uzly a hneď ako existuje modul, prejde sa na cielené observery. */
    let knownModules = 0;
    let bootFrame = 0;
    /* FIX 193: pri prekresľovaní kolekcie / lazy-load obrázkov prišli stovky
       mutation records za sekundu a každý spúšťal querySelectorAll nad celým
       dokumentom. Prácu zbierame do jedného rámca. */
    const bootObserver = new MutationObserver(() => {
      if (bootFrame) return;
      bootFrame = window.requestAnimationFrame(() => {
        bootFrame = 0;
        const count = this.modules().length;
        if (count === knownModules) return;
        knownModules = count;
        this.observeModules();
        this.applyOversizeToDom();
        this.scheduleApply();
      });
    });
    bootObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
    this.observers.push({ scope: 'boot', observer: bootObserver });

    /* FIX 193: pôvodne sa na KAŽDÝ cart event (vrátane vlastných, ktoré
       posiela AOCartBenefitsManager) hneď volalo `syncFromCart()` — teda
       fetch /cart.js. Jedno pridanie do košíka tak vygenerovalo sériu
       požiadaviek; Shopify ich pri /cart.js obmedzuje a ďalšie pridanie
       potom reálne zlyhá. Vlastné eventy preskakujeme a zvyšok zlúčime. */
    let cartEventTimer = 0;
    ['cart:update', 'cart:refresh', 'cart:change', 'cart:updated'].forEach((name) => {
      document.addEventListener(name, (event) => {
        if (event?.detail?.data?.source === 'ao-cart-benefits') return;
        window.clearTimeout(cartEventTimer);
        cartEventTimer = window.setTimeout(() => {
          this.observeModules();
          const snapshot = event?.detail?.resource || event?.detail?.cart || null;
          const isCompleteCart = snapshot
            && Array.isArray(snapshot.items)
            && typeof snapshot.item_count === 'number'
            && snapshot.attributes;
          this.syncFromCart(isCompleteCart ? snapshot : null);
        }, 60);
      });
    });

    window.addEventListener('pageshow', () => {
      if (pageCartCount() > 0) runWhenIdle(() => this.syncFromCart(), 900);
    });

    this.observeModules();
    /* Liquid už pozná nadrozmer pri prvom rendri. Použime ho okamžite a
       sieťový cart.js nech nekonkuruje CSS/hero assetom počas first paint. */
    this.oversize = this.modules().some((module) => module.dataset.aoOversize === 'true');
    this.apply();
    if (pageCartCount() > 0) runWhenIdle(() => this.syncFromCart(), 900);
  }
}

/* =========================================================
   GIFT + SHIPPING PROTECTION
   ========================================================= */

/* Hranica sa berie z nastavení témy (snippets/scripts.liquid). */
function giftThreshold() {
  const node = document.getElementById('ao-cart-benefits-config');
  if (node) {
    try {
      const parsed = JSON.parse(node.textContent || '{}');
      const value = Number(parsed.giftThreshold);
      if (Number.isFinite(value) && value > 0) return value;
    } catch (_) { /* padáme na predvolenú hodnotu nižšie */ }
  }
  return GIFT_THRESHOLD_FALLBACK;
}

function isExcludedItem(item) {
  const properties = item?.properties || {};
  return isProtectionItem(item)
    || isGiftItem(item)
    || String(properties['_AO exclude-cart-threshold'] || '').toLowerCase() === 'true'
    || String(properties['_AO exclude-delivery-date'] || '').toLowerCase() === 'true'
    || Boolean(String(properties['_AO technický doplnok'] || '').trim())
    || Number(item?.final_line_price || 0) <= 0;
}

function qualifyingCartValue(cart) {
  return (cart?.items || []).reduce((total, item) => (
    isExcludedItem(item) ? total : total + Number(item.final_line_price || 0)
  ), 0);
}

function rewardCartValue(cart) {
  return (cart?.items || []).reduce((total, item) => {
    if (isGiftItem(item) || Number(item?.final_line_price || 0) <= 0) return total;
    if (isProtectionItem(item)) return total + Number(item.final_line_price || 0);
    return isExcludedItem(item) ? total : total + Number(item.final_line_price || 0);
  }, 0);
}

function protectionTier(value) {
  const cents = Math.max(0, Number(value) || 0);
  let tier = PROTECTION_TIERS[0];
  for (const candidate of PROTECTION_TIERS) {
    if (cents >= candidate.min) tier = candidate;
  }
  return tier;
}

function parseVariantMap(raw) {
  const variants = new Map();
  String(raw || '').split(',').forEach((record) => {
    const [sku, id, price] = record.split(':');
    if (sku && Number(id)) variants.set(sku.toUpperCase(), { id: Number(id), price: Number(price) || 0 });
  });
  return variants;
}

class AOCartBenefitsManager {
  constructor() {
    this.root = window.Shopify?.routes?.root || '/';
    this.queue = Promise.resolve(false);
    this.catalogPromise = null;
    this.reconcileTimer = 0;
    this.pendingCart = null;
    this.bound = false;
  }

  readConfig() {
    let globalConfig = {};
    const globalNode = document.getElementById('ao-cart-benefits-config');
    if (globalNode) {
      try { globalConfig = JSON.parse(globalNode.textContent || '{}'); } catch (_) { globalConfig = {}; }
    }

    const element = document.querySelector('[data-ao-cart-benefits]');
    const protectionVariants = parseVariantMap(element?.dataset.protectionVariants);
    const giftId = Number(element?.dataset.giftVariantId || 0);

    return {
      protectionEnabled: globalConfig.protectionEnabled !== false && element?.dataset.protectionEnabled !== 'false',
      giftEnabled: globalConfig.giftEnabled !== false && element?.dataset.giftEnabled !== 'false',
      protectionHandle: element?.dataset.protectionHandle || globalConfig.protectionHandle || 'ochrana-zasielky-plus',
      giftHandle: element?.dataset.giftHandle || globalConfig.giftHandle || 'darcek-k-objednavke',
      protectionVariants,
      giftVariantId: giftId,
    };
  }

  async fetchProduct(handle) {
    if (!handle) return null;
    try {
      const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return null;
      return response.json();
    } catch (_) {
      return null;
    }
  }

  async resolveCatalog(needs = { protection: true, gift: true }) {
    const config = this.readConfig();
    const protectionReady = !needs.protection || config.protectionVariants.size >= PROTECTION_TIERS.length;
    const giftReady = !needs.gift || Boolean(config.giftVariantId);
    if (protectionReady && giftReady) return config;
    if (this.catalogPromise) return this.catalogPromise;

    this.catalogPromise = (async () => {
      const [protectionProduct, giftProduct] = await Promise.all([
        needs.protection && config.protectionEnabled && config.protectionVariants.size < PROTECTION_TIERS.length
          ? this.fetchProduct(config.protectionHandle)
          : null,
        needs.gift && config.giftEnabled && !config.giftVariantId ? this.fetchProduct(config.giftHandle) : null,
      ]);

      for (const variant of protectionProduct?.variants || []) {
        const sku = String(variant.sku || '').trim().toUpperCase();
        if (sku.startsWith('SHIP-PROTECT-')) {
          config.protectionVariants.set(sku, { id: Number(variant.id), price: Number(variant.price) || 0 });
        }
      }

      const giftVariant = (giftProduct?.variants || []).find((variant) => (
        String(variant.sku || '').trim().toUpperCase() === GIFT_SKU
      ));
      if (giftVariant) config.giftVariantId = Number(giftVariant.id);
      return config;
    })().finally(() => { this.catalogPromise = null; });

    return this.catalogPromise;
  }

  async getCart() {
    const response = await fetch(`${this.root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('cart-fetch-failed');
    return response.json();
  }

  sectionRequestData() {
    const ids = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const sections = Array.from(new Set(ids));
    return sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {};
  }

  setBusy(state) {
    document.querySelectorAll('[data-ao-protection-toggle]').forEach((button) => {
      button.disabled = state;
    });
    document.querySelectorAll('[data-ao-cart-benefits]').forEach((element) => {
      element.setAttribute('aria-busy', state ? 'true' : 'false');
    });
  }

  showError(show, message) {
    document.querySelectorAll('[data-ao-benefits-error]').forEach((element) => {
      if (message) element.textContent = message;
      element.hidden = !show;
    });
  }

  formatMoney(cents) {
    if (window.AOMoney) return window.AOMoney.format(cents);
    return (Number(cents || 0) / 100).toFixed(2).replace('.', ',') + '\u00a0€';
  }

  updateProtectionPrice(catalog, cart) {
    const tier = protectionTier(qualifyingCartValue(cart));
    const actual = catalog.protectionVariants.get(tier.sku);
    const cents = actual?.price || tier.price;
    document.querySelectorAll('[data-ao-protection-price]').forEach((element) => {
      element.textContent = `+ ${this.formatMoney(cents)}`;
    });
  }

  async updateLines(updates) {
    if (!Object.keys(updates).length) return null;
    const response = await fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates, ...this.sectionRequestData() }),
    });
    if (!response.ok) throw new Error('cart-update-failed');
    return response.json();
  }

  async addLines(items) {
    if (!items.length) return null;
    const response = await fetch(`${this.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items, ...this.sectionRequestData() }),
    });
    if (!response.ok) throw new Error('cart-add-failed');
    return response.json();
  }

  dispatchRefresh(cart, sections = null) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        data: {
          source: 'ao-cart-benefits',
          itemCount: Number(cart.item_count) || 0,
          sections: sections || undefined,
        },
      },
    }));
    window.setTimeout(() => window.AODelivery?.refresh?.(document), 0);
  }

  /* Kontrola invariantov po zosúladení. Bez nej mohol checkout odísť
     s neaktuálnym košíkom (duplicitná ochrana, chýbajúci darček). */
  verifyInvariants(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const protectionLines = items.filter(isProtectionItem);
    const giftLines = items.filter(isGiftItem);
    if (protectionLines.length > 1) return 'duplicate-protection';
    if (giftLines.length > 1) return 'duplicate-gift';
    if (protectionLines.some((item) => Number(item.quantity) !== 1)) return 'protection-quantity';
    if (giftLines.some((item) => Number(item.quantity) !== 1)) return 'gift-quantity';
    return '';
  }

  /* Odošle košík reálnym submit tlačidlom.
     Globe mení pôvodné CTA na type="button"; requestSubmit() taký prvok
     odmieta (TypeError) a prvé kliknutie sa stratilo. */
  submitCheckout(trigger) {
    const formAttribute = trigger.getAttribute ? trigger.getAttribute('form') : null;
    const form = trigger.form
      || (trigger.closest ? trigger.closest('form') : null)
      || (formAttribute ? document.getElementById(formAttribute) : null);

    if (!form) {
      trigger.dataset.aoBenefitsDone = 'true';
      trigger.click();
      return;
    }

    const proxy = document.createElement('button');
    proxy.type = 'submit';
    proxy.name = 'checkout';
    proxy.value = trigger.getAttribute('value') || '';
    proxy.dataset.aoBenefitsDone = 'true';
    proxy.dataset.aoCheckoutProxy = 'true';
    proxy.setAttribute('aria-hidden', 'true');
    proxy.tabIndex = -1;
    proxy.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none';
    form.appendChild(proxy);

    trigger.dataset.aoBenefitsDone = 'true';
    try {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(proxy);
      else proxy.click();
    } catch (error) {
      /* Nezostávaj v zamknutom stave — vráť CTA do hry a nahlás chybu. */
      delete trigger.dataset.aoBenefitsDone;
      window.AOAnalytics?.track?.('checkout_submit_failed', { message: String(error?.message || error) });
      this.showError(true, 'Objednávku sa nepodarilo otvoriť. Skús to, prosím, znova.');
    } finally {
      proxy.remove();
    }
  }

  async finishAndCheckout(trigger) {
    if (window.AOGlobeGuard && !(await window.AOGlobeGuard.beforeCheckout(trigger))) return;
    if (trigger.dataset.aoCheckoutBusy === 'true') return;

    trigger.dataset.aoCheckoutBusy = 'true';
    trigger.disabled = true;
    trigger.setAttribute('aria-busy', 'true');

    let cart = null;
    let failed = false;
    try {
      await this.enqueue();
      cart = await this.getCart();
    } catch (error) {
      failed = true;
      console.error('[AO cart benefits] reconcile failed', error);
    }

    trigger.disabled = false;
    trigger.removeAttribute('aria-busy');
    delete trigger.dataset.aoCheckoutBusy;

    const invariant = cart ? this.verifyInvariants(cart) : 'cart-unavailable';
    if (failed || invariant) {
      this.showError(true, 'Košík sa nepodarilo zosúladiť (darček / ochrana zásielky). Skús to, prosím, znova.');
      window.AOAnalytics?.track?.('checkout_blocked_reconcile', { reason: invariant || 'reconcile-failed' });
      return;
    }

    this.showError(false);
    this.submitCheckout(trigger);
  }

  async perform(options = {}) {
    this.setBusy(true);
    this.showError(false);
    try {
      let cart = Array.isArray(options.cart?.items) && typeof options.cart?.item_count === 'number'
        ? options.cart
        : await this.getCart();
      const qualifyingValue = qualifyingCartValue(cart);
      const currentRewardValue = rewardCartValue(cart);
      const protectionLines = cart.items.filter(isProtectionItem);
      const giftLines = cart.items.filter(isGiftItem);
      const currentProtection = protectionLines.length > 0;
      const basicConfig = this.readConfig();
      const wantsProtection = qualifyingValue > 0
        && basicConfig.protectionEnabled
        && (typeof options.protectionChoice === 'boolean' ? options.protectionChoice : currentProtection);
      const tier = protectionTier(qualifyingValue);
      const currentProtectionValue = protectionLines.reduce((total, item) => total + Number(item.final_line_price || 0), 0);
      const projectedProtectionValue = wantsProtection ? tier.price : 0;
      const projectedRewardValue = currentRewardValue - currentProtectionValue + projectedProtectionValue;
      const wantsGift = basicConfig.giftEnabled && projectedRewardValue >= giftThreshold();
      const needsCatalog = wantsProtection || wantsGift;
      const catalog = needsCatalog
        ? await this.resolveCatalog({ protection: wantsProtection, gift: wantsGift })
        : basicConfig;
      const existingTierLine = protectionLines.find((item) => skuOf(item) === tier.sku);
      const targetProtection = catalog.protectionVariants.get(tier.sku) || (existingTierLine
        ? { id: Number(existingTierLine.variant_id), price: Number(existingTierLine.final_price) || tier.price }
        : null);
      const targetGiftVariantId = catalog.giftVariantId || Number(giftLines[0]?.variant_id || 0);
      if (wantsProtection && !targetProtection) throw new Error(`missing-protection-variant:${tier.sku}`);
      if (wantsGift && !targetGiftVariantId) throw new Error(`missing-gift-variant:${GIFT_SKU}`);
      const updates = {};
      let addProtection = false;
      let addGift = false;

      const correctProtection = wantsProtection && targetProtection
        ? protectionLines.filter((item) => Number(item.variant_id) === targetProtection.id)
        : [];
      const keepProtectionKey = correctProtection.length ? correctProtection[0].key : null;
      for (const item of protectionLines) {
        if (item.key === keepProtectionKey) {
          if (Number(item.quantity) !== 1) updates[item.key] = 1;
        } else {
          updates[item.key] = 0;
        }
      }
      if (wantsProtection && targetProtection && !keepProtectionKey) addProtection = true;

      const correctGift = wantsGift && targetGiftVariantId
        ? giftLines.filter((item) => Number(item.variant_id) === targetGiftVariantId)
        : [];
      const keepGiftKey = correctGift.length ? correctGift[0].key : null;
      for (const item of giftLines) {
        if (item.key === keepGiftKey) {
          if (Number(item.quantity) !== 1) updates[item.key] = 1;
        } else {
          updates[item.key] = 0;
        }
      }
      if (wantsGift && targetGiftVariantId && !keepGiftKey) addGift = true;

      const changed = Object.keys(updates).length > 0 || addProtection || addGift;
      let mutationResult = null;
      let updatedSections = null;
      if (Object.keys(updates).length) {
        mutationResult = await this.updateLines(updates);
        updatedSections = mutationResult?.sections || updatedSections;
      }

      const additions = [];
      if (addProtection) {
        additions.push({
          id: targetProtection.id,
          quantity: 1,
          properties: {
            '_AO technický doplnok': 'Ochrana zásielky+',
            '_AO exclude-cart-threshold': 'true',
            '_AO exclude-delivery-date': 'true',
          },
        });
      }
      if (addGift) {
        additions.push({
          id: targetGiftVariantId,
          quantity: 1,
          properties: {
            '_AO technický doplnok': 'Darček k objednávke',
            '_AO exclude-cart-threshold': 'true',
            '_AO exclude-delivery-date': 'true',
          },
        });
      }
      if (additions.length) {
        mutationResult = await this.addLines(additions);
        updatedSections = mutationResult?.sections || updatedSections;
      }

      if (changed) {
        if (mutationResult?.items && typeof mutationResult.item_count === 'number' && !additions.length) {
          cart = mutationResult;
        } else {
          cart = await this.getCart();
        }
        this.dispatchRefresh(cart, updatedSections);
      }
      this.updateProtectionPrice(catalog, cart);
      return changed;
    } catch (error) {
      console.error('[AO cart benefits]', error);
      this.showError(true, 'Niečo sa nepodarilo. Skús to, prosím, znova.');
      throw error;
    } finally {
      this.setBusy(false);
    }
  }

  enqueue(options = {}) {
    const run = this.queue.then(() => this.perform(options), () => this.perform(options));
    /* Frontu držíme vždy v splnenom stave, aby jedna chyba nezablokovala
       ďalšie kolá. Volajúci dostane skutočný výsledok cez `run`. */
    this.queue = run.catch(() => false);
    return run;
  }

  scheduleReconcile(cart = null) {
    if (Array.isArray(cart?.items) && typeof cart?.item_count === 'number') this.pendingCart = cart;
    window.clearTimeout(this.reconcileTimer);
    this.reconcileTimer = window.setTimeout(() => {
      const pendingCart = this.pendingCart;
      this.pendingCart = null;
      this.enqueue(pendingCart ? { cart: pendingCart } : {}).catch(() => false);
    }, 20);
  }

  bind() {
    if (this.bound) return;
    this.bound = true;

    document.addEventListener('click', async (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const toggle = target?.closest('[data-ao-protection-toggle]');
      if (toggle) {
        event.preventDefault();
        const active = toggle.getAttribute('aria-pressed') === 'true';
        await this.enqueue({ protectionChoice: !active }).catch(() => false);
        return;
      }

      const checkout = target?.closest('[name="checkout"], [data-ao-checkout]');
      if (!checkout || checkout.dataset.aoCheckoutProxy) return;
      if (!checkout.closest('.cart-drawer, .ao-cart-page, .main-cart')) return;
      if (checkout.dataset.aoBenefitsDone === 'true') return;
      event.preventDefault();
      event.stopPropagation();
      await this.finishAndCheckout(checkout);
    }, true);

    document.addEventListener('submit', async (event) => {
      const submitter = event.submitter;
      if (!(submitter instanceof HTMLElement) || submitter.name !== 'checkout') return;
      if (submitter.dataset.aoCheckoutProxy) return;
      if (submitter.dataset.aoBenefitsDone === 'true') return;
      event.preventDefault();
      await this.finishAndCheckout(submitter);
    }, true);

    ['cart:update', 'discount:update', 'cart:refresh', 'cart:change', 'cart:updated'].forEach((name) => {
      document.addEventListener(name, (event) => {
        if (event.detail?.data?.source === 'ao-cart-benefits') return;
        this.scheduleReconcile(event.detail?.resource || event.detail?.cart || null);
      });
    });

    window.addEventListener('pageshow', () => {
      if (pageCartCount() > 0) runWhenIdle(() => this.scheduleReconcile(), 1200);
    });
    /* Prázdny košík nepotrebuje cart.js ani lookup technických produktov.
       Po prvom reálnom pridaní nás aj tak zobudí event cart:update. */
    if (pageCartCount() > 0) runWhenIdle(() => this.scheduleReconcile(), 1200);
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const globeGuard = new AOGlobeDeliveryGuard();
  globeGuard.bind();
  window.AOGlobeGuard = globeGuard;

  const manager = new AOCartBenefitsManager();
  manager.bind();
  window.AOCartBenefits = manager;
  window.AOCartRewards = { rewardValue: rewardCartValue, qualifyingValue: qualifyingCartValue, giftThreshold };
}

export {
  AOGlobeDeliveryGuard,
  AOCartBenefitsManager,
  AO_ATTR,
  SHIPPING_RULE_VERSION_FALLBACK,
  shippingRuleVersion,
  GIFT_SKU,
  GIFT_THRESHOLD_FALLBACK,
  giftThreshold,
  PROTECTION_TIERS,
  cartIsOversize,
  itemIsOversize,
  isExcludedItem,
  isGiftItem,
  isProtectionItem,
  isTechnicalItem,
  protectionTier,
  qualifyingCartValue,
  rewardCartValue,
};
